import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Search, Building2, MapPin, Phone, Mail, Percent, Calendar, ToggleLeft, ToggleRight } from 'lucide-react';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh"
];

const emptyForm = {
  client_name: '',
  gst_number: '',
  billing_address: '',
  state: '',
  credit_period_days: '30',
  billing_percentage: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  replacement_clause_days: '90',
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/clients?include_disabled=${showDisabled}`);
      setClients(res.data);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, [showDisabled]);

  const filtered = clients.filter(c => 
    c.client_name.toLowerCase().includes(search.toLowerCase()) ||
    c.gst_number?.toLowerCase().includes(search.toLowerCase()) ||
    c.state?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setForm(emptyForm);
    setEditClient(null);
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setForm({
      client_name: client.client_name || '',
      gst_number: client.gst_number || '',
      billing_address: client.billing_address || '',
      state: client.state || '',
      credit_period_days: client.credit_period_days?.toString() || '30',
      billing_percentage: client.billing_percentage?.toString() || '',
      contact_person: client.contact_person || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      replacement_clause_days: client.replacement_clause_days?.toString() || '90',
    });
    setEditClient(client);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.client_name || !form.billing_address || !form.state) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        credit_period_days: parseInt(form.credit_period_days),
        billing_percentage: form.billing_percentage ? parseFloat(form.billing_percentage) : null,
        replacement_clause_days: parseInt(form.replacement_clause_days),
      };
      
      if (editClient) {
        await api.put(`/finance/clients/${editClient.id}`, payload);
        toast.success('Client updated');
      } else {
        await api.post('/finance/clients', payload);
        toast.success('Client created');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleClientStatus = async (client) => {
    try {
      if (client.is_active) {
        await api.put(`/finance/clients/${client.id}/disable`);
        toast.success('Client disabled');
      } else {
        await api.put(`/finance/clients/${client.id}/enable`);
        toast.success('Client enabled');
      }
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Clients</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage billing clients</p>
        </div>
        <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="pl-10 rounded-sm"
            data-testid="client-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={showDisabled} onCheckedChange={setShowDisabled} />
          <span className="text-sm text-muted-foreground">Show disabled</span>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Client Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">GST Number</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">State</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Billing %</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Credit Days</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Contact</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No clients found</TableCell></TableRow>
            ) : filtered.map(client => (
              <TableRow key={client.id} className={!client.is_active ? 'opacity-50' : ''} data-testid={`client-row-${client.id}`}>
                <TableCell className="font-medium">{client.client_name}</TableCell>
                <TableCell className="font-mono text-xs">{client.gst_number || '-'}</TableCell>
                <TableCell>{client.state}</TableCell>
                <TableCell className="font-mono">{client.billing_percentage != null ? `${client.billing_percentage}%` : '-'}</TableCell>
                <TableCell>{client.credit_period_days} days</TableCell>
                <TableCell>
                  <div className="text-sm">{client.contact_person || '-'}</div>
                  <div className="text-xs text-muted-foreground">{client.contact_email}</div>
                </TableCell>
                <TableCell>
                  <Badge className={client.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}>
                    {client.is_active ? 'Active' : 'Disabled'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(client)} data-testid={`edit-client-${client.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleClientStatus(client)}
                      data-testid={`toggle-client-${client.id}`}
                    >
                      {client.is_active ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
            <DialogDescription>Fill in the client details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client Name *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={form.client_name} onChange={e => set('client_name', e.target.value)} className="pl-10 rounded-sm" placeholder="Company Name" data-testid="input-client-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">GST Number</Label>
                <Input value={form.gst_number} onChange={e => set('gst_number', e.target.value)} className="rounded-sm font-mono" placeholder="27AABCU9603R1ZM" data-testid="input-gst" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">State *</Label>
                <Select value={form.state} onValueChange={v => set('state', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Billing Address *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input value={form.billing_address} onChange={e => set('billing_address', e.target.value)} className="pl-10 rounded-sm" placeholder="Full billing address" data-testid="input-address" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Billing %</Label>
                <div className="relative">
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={form.billing_percentage} onChange={e => set('billing_percentage', e.target.value)} className="pl-10 rounded-sm" placeholder="8.33" data-testid="input-billing-pct" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Credit Days</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={form.credit_period_days} onChange={e => set('credit_period_days', e.target.value)} className="pl-10 rounded-sm" placeholder="30" data-testid="input-credit-days" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Guarantee Days</Label>
                <Input type="number" value={form.replacement_clause_days} onChange={e => set('replacement_clause_days', e.target.value)} className="rounded-sm" placeholder="90" data-testid="input-guarantee-days" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contact Person</Label>
              <Input value={form.contact_person} onChange={e => set('contact_person', e.target.value)} className="rounded-sm" placeholder="John Doe" data-testid="input-contact-person" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} className="pl-10 rounded-sm" placeholder="john@company.com" data-testid="input-contact-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} className="pl-10 rounded-sm" placeholder="+91 9876543210" data-testid="input-contact-phone" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="save-client-btn">
              {saving ? 'Saving...' : editClient ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
